import { ArrowSquareOutIcon, CopyIcon, LinkIcon } from "@phosphor-icons/react";
import { useTranslate } from "@refinedev/core";
import { Button, Card, Col, Input, message, Row, Typography } from "antd";
import { useAntTheme } from "hooks/useAntTheme";
import React from "react";
import styled from "styled-components";
import ShareButtons from "./ShareButtons";
import { useAnalytics } from "hooks/useAnalytics";
import { AnalyticsEvents } from "types/analytics/";
import { useSoftPaywall } from "@/providers/SoftPaywallProvider";
import { radius } from "@libertify/ui";

const ActionButton = styled(Button)`
  background-color: ${({ theme }) => theme.colorWhite};
  border-radius: ${radius.pill}px;
  box-shadow: 0 2px 30px 0 #00000017;
  font-size: 16px;
  color: ${({ theme }) => theme.colorBlack};
  letter-spacing: -3%;
  line-height: 100%;
  min-width: 120px;
  &:hover,
  &:focus {
    background-color: ${({ theme }) => theme.colorWhite};
    color: ${({ theme }) => theme.colorBlack};
    opacity: 0.9;
  }
`;

interface ProjectShareProps {
  projectUrl?: string;
  projectId?: string;
  instanceId?: string;
}

export const ProjectShare: React.FC<ProjectShareProps> = ({
  projectUrl,
  projectId,
  instanceId,
}) => {
  const t = useTranslate();
  const { theme } = useAntTheme();
  const { trackEvent } = useAnalytics();
  const { paywallFlow } = useSoftPaywall();

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(projectUrl);
      message.success(t("media.components.ProjectShare.copySuccess"));
      trackEvent(AnalyticsEvents.ProjectSharingLinkCopy, {
        project_id: projectId,
        instance_id: instanceId,
      });
      trackEvent(AnalyticsEvents.ProjectShareIntent, {
        project_id: projectId,
        instance_id: instanceId,
        method: "clipboard",
        paywall_flow: paywallFlow,
      });
    } catch {
      message.error(t("media.components.ProjectShare.copyError"));
    }
  };

  const handleOpen = () => {
    // TODO: add analytics event
    window.open(projectUrl, "_blank");
  };

  return (
    <Card
      style={{
        borderRadius: radius.box,
        backgroundColor: theme.colorCardRedesign,
        border: theme.cardBorder,
        marginBottom: "25px",
        boxShadow: theme.cardBoxShadow,
      }}
      styles={{
        body: { padding: "25px 20px" },
      }}
    >
      <Row gutter={[6, 12]} align={"top"}>
        <Col
          xs={24}
          md={24}
          lg={4}
          style={{
            display: "flex",
            alignItems: "center",
          }}
        >
          <LinkIcon
            size={30}
            color={theme.colorTextBaseRedesign}
            style={{
              marginRight: "8px",
            }}
          />
          <Typography.Text
            style={{ color: theme.colorTextBaseRedesign, fontSize: "18px" }}
          >
            {t("media.components.ProjectShare.projectShareLink")}
          </Typography.Text>
        </Col>
        <Col xs={24} md={24} lg={14}>
          <Row justify={"center"} gutter={[0, 16]}>
            <StyledInput value={projectUrl} onClick={handleCopy} readOnly />
            <ShareButtons
              url={projectUrl}
              iconSize={28}
              iconColor={theme.colorTextDescription}
              onClick={(network, _url) => {
                trackEvent(AnalyticsEvents.ProjectShareIntent, {
                  project_id: projectId,
                  instance_id: instanceId,
                  // normalize network name to lowercase to easier lookup in analytics
                  method: network.toLocaleLowerCase(),
                  paywall_flow: paywallFlow,
                });
              }}
            />
          </Row>
        </Col>
        <Col
          xs={24}
          md={24}
          lg={6}
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <Row justify="center" gutter={[8, 8]}>
            <Col>
              <ActionButton icon={<CopyIcon />} onClick={handleCopy}>
                {t("media.components.ProjectShare.copy")}
              </ActionButton>
            </Col>
            <Col>
              <Button
                type="primary"
                shape="round"
                icon={<ArrowSquareOutIcon size={20} />}
                onClick={handleOpen}
              >
                {t("media.components.ProjectShare.open")}
              </Button>
            </Col>
          </Row>
        </Col>
      </Row>
    </Card>
  );
};

const StyledInput = styled(Input)`
  width: 100%;
  background-color: ${({ theme }) => theme.colorCardRedesign};
  color: ${({ theme }) => theme.colorTextBaseRedesign};
  border-radius: 10px;
  border: 1px solid #d9d9d9;
  text-align: center;
  box-shadow: 0 0 20px rgba(0, 0, 0, 0.125);
  &:hover {
    border-color: ${({ theme }) => theme.colorPrimaryHover};
    color: ${({ theme }) => theme.colorPrimaryHover};
    cursor: pointer;
  }
`;
